import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'github-markdown-css/github-markdown.css';

import api from '../api/axios';
import useLocale from '../hooks/useLocale';
import './HelpButton.css';

export default function HelpButton({ helpId, helpLabel }) {
  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tip, setTip] = useState(null);
  const [error, setError] = useState(null);

  const titleText = useMemo(() => helpLabel || 'Help', [helpLabel]);

  const open = async () => {
    setIsOpen(true);
    setLoading(true);
    setError(null);
    setTip(null);

    try {
      const res = await api.get('/help/tips', { params: { id: helpId, lang } });
      setTip(res.data);
    } catch (e) {
      // If not found -> show “not filled yet” message
      const status = e?.response?.status;
      if (status === 404) {
        setError(
          lang === 'ru' ? 'Подсказка ещё не заполнена.' : 'This tip hasn’t been filled yet.',
        );
      } else {
        setError(lang === 'ru' ? 'Ошибка загрузки подсказки.' : 'Failed to load help tip.');
      }
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setIsOpen(false);
    setTip(null);
    setError(null);
    setLoading(false);
  };

  const normalizeUrl = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return null;

    // already absolute
    if (/^https?:\/\//i.test(s)) return s;

    // protocol-relative
    if (s.startsWith('//')) return `https:${s}`;

    // common case: "youtube.com/..." or "www.youtube.com/..." or "youtu.be/..."
    // treat as https
    return `https://${s}`;
  };

  const renderVideo = (rawUrl) => {
    const u0 = normalizeUrl(rawUrl);
    if (!u0) return null;

    let parsed;
    try {
      parsed = new URL(u0);
    } catch (e) {
      // if still invalid, just render a clickable link as fallback
      return (
        <a href={u0} target="_blank" rel="noreferrer">
          {u0}
        </a>
      );
    }

    const host = (parsed.hostname || '').toLowerCase();
    const href = parsed.href;

    // YouTube watch link -> embed
    if (host.includes('youtube.com')) {
      if (parsed.pathname === '/watch') {
        const id = parsed.searchParams.get('v');
        if (id) {
          const embed = `https://www.youtube.com/embed/${id}`;
          return (
            <iframe
              title="help-video"
              src={embed}
              width="100%"
              height="360"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ border: 'none', borderRadius: 8 }}
            />
          );
        }
      }

      // already embed or other youtube path - still try to show inline
      return (
        <iframe
          title="help-video"
          src={href}
          width="100%"
          height="360"
          style={{ border: 'none', borderRadius: 8 }}
          allowFullScreen
        />
      );
    }

    // youtu.be short link -> embed
    if (host === 'youtu.be') {
      const id = parsed.pathname.replace('/', '').split('/')[0];
      if (id) {
        const embed = `https://www.youtube.com/embed/${id}`;
        return (
          <iframe
            title="help-video"
            src={embed}
            width="100%"
            height="360"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ border: 'none', borderRadius: 8 }}
          />
        );
      }
    }

    // mp4/webm -> native video
    if (href.toLowerCase().endsWith('.mp4') || href.toLowerCase().endsWith('.webm')) {
      return (
        <video controls style={{ width: '100%', borderRadius: 8 }}>
          <source src={href} />
        </video>
      );
    }

    // fallback inline iframe
    return (
      <iframe
        title="help-video"
        src={href}
        width="100%"
        height="360"
        style={{ border: 'none', borderRadius: 8 }}
      />
    );
  };

  return (
    <>
      <button
        type="button"
        className="help-btn"
        title={titleText}
        aria-label={titleText}
        onClick={open}
      >
        ?
      </button>

      {isOpen ? (
        <div className="help-modal-backdrop" onMouseDown={close} role="presentation">
          <div className="help-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="help-modal-head">
              <div className="help-modal-title">{helpLabel || helpId}</div>
              <button type="button" className="help-modal-close" onClick={close} aria-label="Close">
                ×
              </button>
            </div>

            <div className="help-modal-body">
              {loading ? (
                <div className="help-muted">{lang === 'ru' ? 'Загрузка…' : 'Loading…'}</div>
              ) : error ? (
                <div className="help-muted">{error}</div>
              ) : tip ? (
                <>
                  {tip.title ? <div className="help-tip-title">{tip.title}</div> : null}
                  {tip.body_md ? (
                    <div className="help-markdown markdown-body">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{tip.body_md}</ReactMarkdown>
                    </div>
                  ) : null}
                  {tip.video_url ? (
                    <div className="help-video">{renderVideo(tip.video_url)}</div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
